import { RefObject, useState, useEffect, useCallback, useRef } from 'react'

export interface useIntersectionObserverOptions {
  root?: Element | null
  rootMargin?: string
  threshold?: number | number[]
}

interface useIntersectionObserverReturn<T extends HTMLElement> {
  isVisible: boolean
  observedElementRef: RefObject<T>
  disconnect: () => void
}

/**
 * The `useIntersectionObserver` function is a TypeScript hook that allows you to track the
 * visibility of a specified element using the Intersection Observer API.
 * @param {useIntersectionObserverOptions}
 * - `root`
 * - `rootMargin`
 * - `threshold`
 * @returns The function `useIntersectionObserver` returns an object with the following properties:
 * - `isVisible`: A boolean that indicates whether the target element is visible.
 * - `observedElementRef`: A React ref that should be attached to the target element.
 * - `disconnect`: A function that disconnects the IntersectionObserver.
 * @example using the hook with HTMLButtonElement as the generic type
 * import { useIntersectionObserver } from 'src/hooks'
 * const { isVisible, observedElementRef, disconnect } = useIntersectionObserver<HTMLButtonElement>()
 */
export const useIntersectionObserver = <T extends HTMLElement>({
  root = null,
  rootMargin = '0px',
  threshold = 1.0,
}: useIntersectionObserverOptions = {}): useIntersectionObserverReturn<T> => {
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const observedElementRef = useRef<T | null>(null)

  const callback = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries
    setIsVisible(entry.isIntersecting)
  }, [])

  const observer = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    observer.current = new IntersectionObserver(callback, {
      root,
      rootMargin,
      threshold,
    })

    const currentElement = observedElementRef.current
    if (currentElement) {
      observer.current.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        observer?.current?.unobserve(currentElement)
      }
    }
  }, [observedElementRef, root, rootMargin, threshold, callback])

  const disconnect = useCallback(() => {
    observer.current?.disconnect()
  }, [observer])

  return { isVisible, observedElementRef, disconnect }
}

export default useIntersectionObserver
